import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import Button from "react-bootstrap/esm/Button";
import { useAuth } from "../hooks/useAuth";
import { auth } from "../services/firebase";
import { logout } from "../services/firebaseAuthService";
import { Globals } from "../globals";

/**
 * Holding page for users who are authenticated but not yet assigned to any
 * Gemeinde. They have no data access (the security rules deny it) until an
 * admin assigns them (GOD-43): the assignment arrives by itself (accountSignals)
 * or with "Erneut prüfen"; until then the page names whom to contact.
 */
export default function NoGemeinde() {
    const { t } = useTranslation();
    const { user, gemeindeId, isSuperAdmin } = useAuth();
    const navigate = useNavigate();
    const [checking, setChecking] = useState(false);
    const [stillNothing, setStillNothing] = useState(false);

    // An admin assigning this account sends a signal; AppLayout's useClaimsRefresh
    // fetches the fresh token and this effect moves on.
    useEffect(() => {
        if (isSuperAdmin) navigate(Globals.PATH_SUPERADMIN, { replace: true });
        else if (gemeindeId) navigate(Globals.PATH_SIMPLE_SUNDAY_SERVICE_LIST, { replace: true });
    }, [gemeindeId, isSuperAdmin, navigate]);

    async function handleRecheck() {
        setChecking(true);
        setStillNothing(false);
        try {
            const result = await auth.currentUser?.getIdTokenResult(true);
            if (!result?.claims.gemeindeId) setStillNothing(true);
        } finally {
            setChecking(false);
        }
    }

    async function handleLogout() {
        await logout();
        navigate(Globals.PATH_DEFAULT, { state: { fromLogout: true } });
    }

    const mailto = `mailto:${Globals.SUPPORT_EMAIL}?subject=${encodeURIComponent(t("noGemeinde.mailSubject") as string)}`;

    return (
        <div className="gl-login-wrap">
            <div className="gl-login-card text-center">
                <div className="gl-wordmark mb-3" style={{ fontSize: 28 }}>
                    Serva
                </div>
                <h1 className="mb-2" style={{ fontSize: "1.4rem" }}>{t("noGemeinde.title")}</h1>
                <p className="text-muted mb-3">{t("noGemeinde.body")}</p>
                {user?.email && (
                    <p className="gl-meta mb-3">
                        {t("noGemeinde.signedInAs")}: <code>{user.email}</code>
                    </p>
                )}
                <p className="small mb-4">
                    {t("noGemeinde.contact")} <a href={mailto}>{Globals.SUPPORT_EMAIL}</a>
                </p>
                {stillNothing && (
                    <p className="small text-warning" role="status">
                        {t("noGemeinde.stillNothing")}
                    </p>
                )}
                <div className="d-flex justify-content-center gap-2">
                    <Button variant="primary" onClick={handleRecheck} disabled={checking}>
                        {t("noGemeinde.recheck")}
                    </Button>
                    <Button variant="outline-secondary" onClick={handleLogout}>
                        {t("common.logout")}
                    </Button>
                </div>
            </div>
        </div>
    );
}
